import React, { useState, useEffect } from 'react';
import axiosInstance from '../../axios/axiosInstance';
import BrandingImageField from '../../components/BrandingImageField';

const STATUS_COLORS = {
    active: { bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
    trial:  { bg: '#fefce8', color: '#ca8a04', border: '#fde047' },
    past_due: { bg: '#fffbeb', color: '#d97706', border: '#fcd34d' },
    suspended: { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
};

const DOMAIN_STATUS = {
    none: { label: 'Not set', bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
    pending_dns: { label: 'Pending DNS', bg: '#fffbeb', color: '#b45309', border: '#fcd34d' },
    provisioning: { label: 'Provisioning', bg: '#eff6ff', color: '#1d4ed8', border: '#93c5fd' },
    live: { label: 'Live', bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
    failed: { label: 'Failed', bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
    removing: { label: 'Removing', bg: '#f8fafc', color: '#64748b', border: '#cbd5e1' },
};

const TYPE_LABELS = { platform: 'Platform', reseller: 'Reseller', office: 'Office' };

const EMPTY_FORM = {
    name: '', type: 'reseller', email: '', phone: '', ein: '',
    subdomain: '', customDomain: '', customDomainStatus: 'none', customDomainError: '',
    status: 'active', planId: '',
    maxCompanies: 25, maxEmployees: 200, maxUsers: 15,
    productName: '', primaryColor: '#1B75F0', darkColor: '#051147',
    logoUrl: '', loginLogoUrl: '', faviconUrl: '',
    ownerEmail: '', ownerPassword: '', ownerName: '',
};

const Field = ({ label, children }) => (
    <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>
            {label}
        </label>
        {children}
    </div>
);

const Input = ({ value, onChange, placeholder, type = 'text', style = {} }) => (
    <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
            width: '100%', padding: '8px 10px', borderRadius: 7,
            border: '1px solid #d1d5db', fontSize: 13,
            outline: 'none', boxSizing: 'border-box', ...style,
        }}
    />
);

const Select = ({ value, onChange, children }) => (
    <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
            width: '100%', padding: '8px 10px', borderRadius: 7,
            border: '1px solid #d1d5db', fontSize: 13,
            outline: 'none', boxSizing: 'border-box', background: '#fff',
        }}
    >
        {children}
    </select>
);

const Spinner = () => (
    <div style={{ width: 16, height: 16, border: '2px solid #1B75F0', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
);

const FirmManagement = () => {
    const [firms, setFirms] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null); // firm being viewed/edited
    const [form, setForm] = useState(EMPTY_FORM);
    const [isNew, setIsNew] = useState(false);
    const [saving, setSaving] = useState(false);
    const [chargeRunning, setChargeRunning] = useState(false);
    const [chargeHistory, setChargeHistory] = useState([]);
    const [toast, setToast] = useState(null);

    const loadChargeHistory = async (firmId) => {
        if (!firmId) { setChargeHistory([]); return; }
        try {
            const res = await axiosInstance.get(`/billing/history/${firmId}`);
            setChargeHistory(Array.isArray(res.data) ? res.data : []);
        } catch {
            setChargeHistory([]);
        }
    };

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const normalizeFirm = (f) => {
        if (!f || typeof f !== 'object') return f;
        return {
            ...f,
            id: f.id ?? f.Id,
            name: f.name ?? f.Name,
            type: f.type ?? f.Type,
            email: f.email ?? f.Email,
            phone: f.phone ?? f.Phone,
            eIN: f.eIN ?? f.ein ?? f.EIN,
            subdomain: f.subdomain ?? f.Subdomain,
            customDomain: f.customDomain ?? f.CustomDomain,
            customDomainStatus: f.customDomainStatus ?? f.CustomDomainStatus,
            customDomainError: f.customDomainError ?? f.CustomDomainError,
            status: f.status ?? f.Status,
            planId: f.planId ?? f.PlanId,
            maxCompanies: f.maxCompanies ?? f.MaxCompanies,
            maxEmployees: f.maxEmployees ?? f.MaxEmployees,
            maxUsers: f.maxUsers ?? f.MaxUsers,
            productName: f.productName ?? f.ProductName,
            primaryColor: f.primaryColor ?? f.PrimaryColor,
            darkColor: f.darkColor ?? f.DarkColor,
            logoUrl: f.logoUrl ?? f.LogoUrl ?? null,
            loginLogoUrl: f.loginLogoUrl ?? f.LoginLogoUrl ?? null,
            faviconUrl: f.faviconUrl ?? f.FaviconUrl ?? null,
            companyCount: f.companyCount ?? f.CompanyCount ?? 0,
            userCount: f.userCount ?? f.UserCount ?? 0,
        };
    };

    const fetchFirms = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/firms');
            const rows = Array.isArray(res.data) ? res.data.map(normalizeFirm) : [];
            setFirms(rows);
        } catch {
            showToast('Failed to load firms.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFirms();
        axiosInstance.get('/plans/all')
            .then(res => setPlans(Array.isArray(res.data) ? res.data : []))
            .catch(() => setPlans([]));
    }, []);

    const openFirm = (firm) => {
        setIsNew(false);
        setSelected(firm);
        loadChargeHistory(firm.id);
        setForm({
            name:         firm.name         || '',
            type:         firm.type         || 'reseller',
            email:        firm.email        || '',
            phone:        firm.phone        || '',
            ein:          firm.eIN          || '',
            subdomain:    firm.subdomain    || '',
            customDomain: firm.customDomain || '',
            customDomainStatus: firm.customDomainStatus || 'none',
            customDomainError: firm.customDomainError || '',
            status:       firm.status       || 'active',
            planId:       firm.planId != null ? String(firm.planId) : '',
            maxCompanies: firm.maxCompanies ?? 25,
            maxEmployees: firm.maxEmployees ?? 200,
            maxUsers:     firm.maxUsers     ?? 15,
            productName:  firm.productName  || '',
            primaryColor: firm.primaryColor || '#1B75F0',
            darkColor:    firm.darkColor    || '#051147',
            logoUrl:      firm.logoUrl      || firm.LogoUrl      || '',
            loginLogoUrl: firm.loginLogoUrl || firm.LoginLogoUrl || '',
            faviconUrl:   firm.faviconUrl   || firm.FaviconUrl   || '',
            ownerEmail: '', ownerPassword: '', ownerName: '',
        });
    };

    const openNew = () => {
        setIsNew(true);
        setSelected({});
        // Fresh copy — never reuse EMPTY_FORM object reference (avoids cross-firm form bleed).
        setForm({ ...EMPTY_FORM });
    };

    const closePanel = () => { setSelected(null); setIsNew(false); };

    const setField = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

    const handleSave = async () => {
        if (!form.name.trim()) { showToast('Firm name is required.', 'error'); return; }
        setSaving(true);
        try {
            const payload = {
                name: form.name, type: form.type, email: form.email || null,
                phone: form.phone || null, eIN: form.ein || null,
                subdomain: form.subdomain || null,
                // Empty string clears CustomDomain on the API (null would skip the field).
                customDomain: (form.customDomain || '').trim(),
                status: form.status,
                planId: form.planId ? Number(form.planId) : null,
                maxCompanies: Number(form.maxCompanies),
                maxEmployees: Number(form.maxEmployees),
                maxUsers: Number(form.maxUsers),
                productName: form.productName || null,
                primaryColor: form.primaryColor,
                darkColor: form.darkColor,
                logoUrl: form.logoUrl || null,
                loginLogoUrl: form.loginLogoUrl || null,
                faviconUrl: form.faviconUrl || null,
            };
            if (isNew) {
                if (form.ownerEmail.trim()) {
                    payload.ownerEmail = form.ownerEmail.trim();
                    payload.ownerPassword = form.ownerPassword || null;
                    payload.ownerName = form.ownerName.trim() || null;
                    if (!payload.ownerPassword) {
                        showToast('Owner password is required when creating a reseller login.', 'error');
                        setSaving(false);
                        return;
                    }
                }
                const res = await axiosInstance.post('/firms', payload);
                // Create API returns a thin payload — merge form fields so the table row is complete,
                // then refresh from server for authoritative counts/ids.
                const created = {
                    id: res.data.id ?? res.data.Id,
                    name: res.data.name ?? form.name,
                    type: form.type,
                    email: form.email || null,
                    status: form.status,
                    planId: form.planId ? Number(form.planId) : null,
                    subdomain: form.subdomain || null,
                    customDomain: form.customDomain || null,
                    maxCompanies: Number(form.maxCompanies),
                    maxEmployees: Number(form.maxEmployees),
                    maxUsers: Number(form.maxUsers),
                    productName: form.productName || null,
                    primaryColor: form.primaryColor,
                    darkColor: form.darkColor,
                    logoUrl: form.logoUrl || null,
                    companyCount: 0,
                    userCount: form.ownerEmail.trim() ? 1 : 0,
                };
                setFirms(prev => [...prev, created]);
                showToast(res.data.message || `Firm "${created.name}" created.`);
                closePanel();
                fetchFirms();
            } else {
                const firmId = selected.id ?? selected.Id;
                if (firmId == null) {
                    showToast('No firm selected to update.', 'error');
                    setSaving(false);
                    return;
                }
                await axiosInstance.put(`/firms/${firmId}`, payload);
                // Never spread the raw Firm entity into the list — refresh from GetFirms instead.
                showToast('Firm updated.');
                await fetchFirms();
                // Keep the edit panel on the same firm with saved form values (incl. new logo URLs).
                openFirm({
                    ...selected,
                    ...payload,
                    id: firmId,
                    eIN: payload.eIN,
                    logoUrl: payload.logoUrl,
                    loginLogoUrl: payload.loginLogoUrl,
                    faviconUrl: payload.faviconUrl,
                });
            }
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to save firm.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (firm) => {
        if (!window.confirm(`Delete "${firm.name}"? This cannot be undone.`)) return;
        try {
            await axiosInstance.delete(`/firms/${firm.id}`);
            setFirms(prev => prev.filter(f => f.id !== firm.id));
            if (selected?.id === firm.id) closePanel();
            showToast(`Firm "${firm.name}" deleted.`);
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to delete firm.', 'error');
        }
    };

    return (
        <div>
            {toast && (
                <div style={{
                    position: 'fixed', top: 20, right: 24, zIndex: 9999,
                    background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
                    border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
                    color: toast.type === 'error' ? '#dc2626' : '#16a34a',
                    borderRadius: 10, padding: '12px 20px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 14, fontWeight: 500,
                }}>
                    {toast.type === 'error' ? '✗ ' : '✓ '}{toast.msg}
                </div>
            )}

            {/* Page header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
                <div>
                    <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a3a5c', margin: 0 }}>Firm & Reseller Management</h1>
                    <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: 14 }}>
                        Manage reseller firms, white-label branding, and usage limits.
                    </p>
                </div>
                <button
                    onClick={openNew}
                    style={{
                        background: '#1B75F0', color: '#fff', border: 'none',
                        borderRadius: 8, padding: '9px 20px', fontSize: 13,
                        fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                    }}
                >
                    + New Firm
                </button>
            </div>

            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                {/* Firms list */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 60 }}><Spinner /></div>
                    ) : (
                        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                        {['Firm', 'Type', 'Status', 'Companies', 'Users', 'Subdomain', ''].map(h => (
                                            <th key={h} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#64748b', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {firms.map(firm => {
                                        const sc = STATUS_COLORS[firm.status] || STATUS_COLORS.active;
                                        const isActive = selected?.id === firm.id;
                                        return (
                                            <tr
                                                key={firm.id}
                                                onClick={() => openFirm(firm)}
                                                style={{
                                                    borderBottom: '1px solid #f1f5f9',
                                                    background: isActive ? '#F0F7FF' : 'transparent',
                                                    cursor: 'pointer',
                                                    transition: 'background 0.1s',
                                                }}
                                                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#fafafa'; }}
                                                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                                            >
                                                <td style={{ padding: '12px 14px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        {firm.logoUrl ? (
                                                            <img key={`${firm.id}-${firm.logoUrl}`} src={firm.logoUrl} alt={firm.name} style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'contain', border: '1px solid #f0f0f0' }} />
                                                        ) : (
                                                            <div style={{ width: 28, height: 28, borderRadius: 6, background: firm.primaryColor || '#1B75F0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 700 }}>
                                                                {firm.name.charAt(0)}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{firm.name}</div>
                                                            {firm.email && <div style={{ fontSize: 11, color: '#94a3b8' }}>{firm.email}</div>}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '12px 14px', fontSize: 12, color: '#64748b' }}>
                                                    {TYPE_LABELS[firm.type] || firm.type}
                                                </td>
                                                <td style={{ padding: '12px 14px' }}>
                                                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
                                                        {firm.status}
                                                    </span>
                                                    {(firm.collectionsFlag || firm.CollectionsFlag) && (
                                                        <span style={{
                                                            marginLeft: 6, fontSize: 10, fontWeight: 700,
                                                            padding: '2px 7px', borderRadius: 20,
                                                            background: '#fef3c7', color: '#b45309', border: '1px solid #fcd34d',
                                                        }}>
                                                            Collections
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '12px 14px', fontSize: 13, color: '#374151' }}>
                                                    <span style={{ fontWeight: 600 }}>{firm.companyCount}</span>
                                                    <span style={{ color: '#94a3b8', fontSize: 11 }}> / {firm.maxCompanies < 0 ? '∞' : firm.maxCompanies}</span>
                                                </td>
                                                <td style={{ padding: '12px 14px', fontSize: 13, color: '#374151' }}>
                                                    <span style={{ fontWeight: 600 }}>{firm.userCount}</span>
                                                    <span style={{ color: '#94a3b8', fontSize: 11 }}> / {firm.maxUsers < 0 ? '∞' : firm.maxUsers}</span>
                                                </td>
                                                <td style={{ padding: '12px 14px', fontSize: 12, color: '#64748b', fontFamily: 'monospace' }}>
                                                    {firm.subdomain || '—'}
                                                </td>
                                                <td style={{ padding: '12px 14px' }} onClick={e => e.stopPropagation()}>
                                                    {firm.type !== 'platform' && (
                                                        <button
                                                            onClick={() => handleDelete(firm)}
                                                            style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', fontSize: 16, padding: '2px 6px', borderRadius: 4 }}
                                                            onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                                                            onMouseLeave={e => e.currentTarget.style.color = '#d1d5db'}
                                                            title="Delete firm"
                                                        >×</button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {firms.length === 0 && (
                                <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>No firms found.</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Edit / Create panel */}
                {selected !== null && (
                    <div style={{ width: 360, flexShrink: 0, background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        {/* Panel header */}
                        <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#1a3a5c' }}>
                                {isNew ? 'New Firm' : form.name}
                            </span>
                            <button onClick={closePanel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 20, lineHeight: 1 }}>×</button>
                        </div>

                        <div style={{ padding: '16px 18px', overflowY: 'auto', maxHeight: 'calc(100vh - 240px)' }}>
                            {/* Basic info */}
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Basic Info</div>

                            <Field label="Firm Name *">
                                <Input value={form.name} onChange={setField('name')} placeholder="Acme Payroll Partners" />
                            </Field>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                <Field label="Type">
                                    <Select value={form.type} onChange={setField('type')}>
                                        <option value="reseller">Reseller</option>
                                        <option value="office">Office</option>
                                        <option value="platform">Platform</option>
                                    </Select>
                                </Field>
                                <Field label="Status">
                                    <Select value={form.status} onChange={setField('status')}>
                                        <option value="active">Active</option>
                                        <option value="trial">Trial</option>
                                        <option value="past_due">Past due (grace)</option>
                                        <option value="suspended">Suspended</option>
                                    </Select>
                                </Field>
                            </div>
                            <Field label="Billing Plan">
                                <Select value={form.planId} onChange={setField('planId')}>
                                    <option value="">— No plan —</option>
                                    {plans.map(p => (
                                        <option key={p.id} value={String(p.id)}>
                                            {p.name}{p.isActive === false ? ' (inactive)' : ''}
                                        </option>
                                    ))}
                                </Select>
                            </Field>
                            <Field label="Email"><Input value={form.email} onChange={setField('email')} placeholder="admin@firm.com" /></Field>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                <Field label="Phone"><Input value={form.phone} onChange={setField('phone')} placeholder="(555) 000-0000" /></Field>
                                <Field label="EIN"><Input value={form.ein} onChange={setField('ein')} placeholder="00-0000000" /></Field>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                <Field label="Subdomain (white-label)">
                                    <Input value={form.subdomain} onChange={setField('subdomain')} placeholder="texascorp" />
                                    {form.subdomain?.trim() ? (
                                        <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
                                            Login URL:{' '}
                                            <a
                                                href={`https://${form.subdomain.trim().toLowerCase()}.payrollnoot.com/login/company`}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ color: '#1B75F0', fontWeight: 600 }}
                                            >
                                                https://{form.subdomain.trim().toLowerCase()}.payrollnoot.com
                                            </a>
                                        </div>
                                    ) : (
                                        <div style={{ marginTop: 6, fontSize: 12, color: '#94a3b8' }}>
                                            Required for white-label: https://{'{subdomain}'}.payrollnoot.com
                                        </div>
                                    )}
                                </Field>
                                <Field label="Custom Domain (optional)">
                                    <Input value={form.customDomain} onChange={setField('customDomain')} placeholder="pay.acme.com" />
                                    {(() => {
                                        const ds = DOMAIN_STATUS[form.customDomainStatus] || DOMAIN_STATUS.none;
                                        return (
                                            <div style={{
                                                marginTop: 6, fontSize: 12, lineHeight: 1.45, padding: 8, borderRadius: 7,
                                                background: ds.bg, border: `1px solid ${ds.border}`, color: ds.color,
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <strong>Provision: {ds.label}</strong>
                                                    {!isNew && selected?.id && form.customDomainStatus === 'failed' && (
                                                        <button
                                                            type="button"
                                                            onClick={async () => {
                                                                try {
                                                                    const res = await axiosInstance.post(`/firms/${selected.id}/custom-domain/retry`);
                                                                    setForm(f => ({
                                                                        ...f,
                                                                        customDomainStatus: res.data.customDomainStatus || 'pending_dns',
                                                                        customDomainError: res.data.customDomainError || '',
                                                                    }));
                                                                    showToast('Retry queued.');
                                                                    fetchFirms();
                                                                } catch (err) {
                                                                    showToast(err?.response?.data?.message || 'Retry failed.', 'error');
                                                                }
                                                            }}
                                                            style={{
                                                                border: `1px solid ${ds.border}`, background: '#fff',
                                                                color: ds.color, borderRadius: 6, padding: '3px 8px',
                                                                fontSize: 11, fontWeight: 600, cursor: 'pointer',
                                                            }}
                                                        >
                                                            Retry
                                                        </button>
                                                    )}
                                                </div>
                                                <div style={{ marginTop: 4, color: '#475569' }}>
                                                    DNS: <strong>CNAME</strong> → <code>payrollnoot.com</code>
                                                    {' '}or <strong>A</strong> → <code>205.209.99.236</code>.
                                                    Prod worker auto-adds IIS + HTTPS (no manual server step).
                                                </div>
                                                {form.customDomainError ? (
                                                    <div style={{ marginTop: 4 }}>{form.customDomainError}</div>
                                                ) : null}
                                                {form.customDomain?.trim() && form.customDomainStatus === 'live' ? (
                                                    <div style={{ marginTop: 4 }}>
                                                        Login:{' '}
                                                        <a
                                                            href={`https://${form.customDomain.trim().toLowerCase().replace(/^https?:\/\//, '').split('/')[0]}/login/company`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            style={{ color: '#1B75F0', fontWeight: 600 }}
                                                        >
                                                            https://{form.customDomain.trim().toLowerCase().replace(/^https?:\/\//, '').split('/')[0]}/login/company
                                                        </a>
                                                    </div>
                                                ) : null}
                                            </div>
                                        );
                                    })()}
                                </Field>
                            </div>

                            {isNew && form.type === 'reseller' && (
                                <>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, margin: '18px 0 12px' }}>
                                        Reseller Owner Login
                                    </div>
                                    <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px' }}>
                                        Creates a user who can sign in at /login/reseller.
                                    </p>
                                    <Field label="Owner Email *">
                                        <Input value={form.ownerEmail} onChange={setField('ownerEmail')} placeholder="owner@reseller.com" />
                                    </Field>
                                    <Field label="Owner Password *">
                                        <Input value={form.ownerPassword} onChange={setField('ownerPassword')} type="password" placeholder="Temp password" />
                                    </Field>
                                    <Field label="Owner Display Name">
                                        <Input value={form.ownerName} onChange={setField('ownerName')} placeholder="Jane Reseller" />
                                    </Field>
                                </>
                            )}

                            {/* Limits */}
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, margin: '18px 0 12px' }}>Usage Limits</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                                <Field label="Max Companies">
                                    <Input value={form.maxCompanies} onChange={setField('maxCompanies')} type="number" />
                                </Field>
                                <Field label="Max Employees">
                                    <Input value={form.maxEmployees} onChange={setField('maxEmployees')} type="number" />
                                </Field>
                                <Field label="Max Users">
                                    <Input value={form.maxUsers} onChange={setField('maxUsers')} type="number" />
                                </Field>
                            </div>

                            {/* Branding */}
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, margin: '18px 0 12px' }}>White-Label Branding</div>
                            <Field label="Product Name"><Input value={form.productName} onChange={setField('productName')} placeholder="PayrollNoot" /></Field>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                <Field label="Primary Color">
                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                        <input type="color" value={form.primaryColor} onChange={e => setField('primaryColor')(e.target.value)}
                                            style={{ width: 36, height: 34, border: '1px solid #d1d5db', borderRadius: 6, padding: 2, cursor: 'pointer' }} />
                                        <Input value={form.primaryColor} onChange={setField('primaryColor')} placeholder="#1B75F0" style={{ flex: 1 }} />
                                    </div>
                                </Field>
                                <Field label="Dark Color">
                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                        <input type="color" value={form.darkColor} onChange={e => setField('darkColor')(e.target.value)}
                                            style={{ width: 36, height: 34, border: '1px solid #d1d5db', borderRadius: 6, padding: 2, cursor: 'pointer' }} />
                                        <Input value={form.darkColor} onChange={setField('darkColor')} placeholder="#051147" style={{ flex: 1 }} />
                                    </div>
                                </Field>
                            </div>
                            <BrandingImageField
                                label="Logo"
                                kind="logo"
                                value={form.logoUrl}
                                onChange={(url) => setForm(f => ({ ...f, logoUrl: url }))}
                            />
                            <BrandingImageField
                                label="Login Logo"
                                kind="loginLogo"
                                value={form.loginLogoUrl}
                                onChange={(url) => setForm(f => ({ ...f, loginLogoUrl: url }))}
                            />
                            <BrandingImageField
                                label="Favicon / Icon"
                                kind="favicon"
                                value={form.faviconUrl}
                                onChange={(url) => setForm(f => ({ ...f, faviconUrl: url }))}
                            />

                            {/* Color preview */}
                            {(form.primaryColor || form.darkColor) && (
                                <div style={{ display: 'flex', gap: 8, marginTop: 4, marginBottom: 16 }}>
                                    <div style={{ flex: 1, height: 32, borderRadius: 6, background: form.primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 600 }}>
                                        Primary
                                    </div>
                                    <div style={{ flex: 1, height: 32, borderRadius: 6, background: form.darkColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 600 }}>
                                        Dark
                                    </div>
                                </div>
                            )}

                            {!isNew && selected?.id && (
                                <>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, margin: '18px 0 12px' }}>
                                        Monthly Charges (Sandbox)
                                    </div>
                                    <p style={{ margin: '0 0 10px', fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>
                                        Charge card on file for the previous calendar month (UTC). Requires plan, payment method, and ended trial.
                                    </p>
                                    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                                        <button
                                            type="button"
                                            disabled={chargeRunning}
                                            onClick={async () => {
                                                if (!window.confirm('Dry-run charges for this firm? No Stripe charge.')) return;
                                                setChargeRunning(true);
                                                try {
                                                    const res = await axiosInstance.post('/billing/run-charges', { firmId: selected.id, dryRun: true });
                                                    const row = res.data?.results?.[0];
                                                    showToast(`${row?.outcome || 'done'}${row?.totalAmount != null ? ` $${Number(row.totalAmount).toFixed(2)}` : ''}${row?.reason || row?.detail ? ` — ${row.reason || row.detail}` : ''}`);
                                                } catch (err) {
                                                    showToast(err?.response?.data?.message || 'Dry run failed.', 'error');
                                                } finally { setChargeRunning(false); }
                                            }}
                                            style={{
                                                flex: 1, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8,
                                                padding: '8px 10px', fontSize: 12, fontWeight: 600, cursor: chargeRunning ? 'wait' : 'pointer', color: '#334155',
                                            }}
                                        >
                                            Dry run
                                        </button>
                                        <button
                                            type="button"
                                            disabled={chargeRunning}
                                            onClick={async () => {
                                                if (!window.confirm('Charge this firm\'s card for the previous month (Stripe sandbox)?')) return;
                                                setChargeRunning(true);
                                                try {
                                                    const res = await axiosInstance.post('/billing/run-charges', { firmId: selected.id, dryRun: false });
                                                    const row = res.data?.results?.[0];
                                                    const isErr = row?.outcome === 'failed' || (row?.outcome === 'skipped' && row?.reason === 'not_eligible');
                                                    showToast(
                                                        `${row?.outcome || 'done'}${row?.totalAmount != null ? ` $${Number(row.totalAmount).toFixed(2)}` : ''}${row?.reason || row?.error || row?.detail ? ` — ${row.reason || row.error || row.detail}` : ''}`,
                                                        isErr ? 'error' : 'success'
                                                    );
                                                    await loadChargeHistory(selected.id);
                                                } catch (err) {
                                                    showToast(err?.response?.data?.message || 'Charge failed.', 'error');
                                                } finally { setChargeRunning(false); }
                                            }}
                                            style={{
                                                flex: 1, background: '#0f766e', border: 'none', borderRadius: 8,
                                                padding: '8px 10px', fontSize: 12, fontWeight: 600, cursor: chargeRunning ? 'wait' : 'pointer', color: '#fff',
                                            }}
                                        >
                                            {chargeRunning ? 'Running…' : 'Run charges'}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={chargeRunning}
                                            onClick={async () => {
                                                if (!window.confirm('FORCE re-charge this firm even if already paid? Creates a NEW Stripe PaymentIntent (sandbox).')) return;
                                                setChargeRunning(true);
                                                try {
                                                    const res = await axiosInstance.post('/billing/run-charges', {
                                                        firmId: selected.id,
                                                        dryRun: false,
                                                        forceRecharge: true,
                                                    });
                                                    const row = res.data?.results?.[0];
                                                    const isErr = row?.outcome === 'failed' || (row?.outcome === 'skipped' && row?.reason === 'not_eligible');
                                                    showToast(
                                                        `${row?.outcome || 'done'}${row?.totalAmount != null ? ` $${Number(row.totalAmount).toFixed(2)}` : ''}${row?.reason || row?.error || row?.detail ? ` — ${row.reason || row.error || row.detail}` : ''}`,
                                                        isErr ? 'error' : 'success'
                                                    );
                                                    await loadChargeHistory(selected.id);
                                                } catch (err) {
                                                    showToast(err?.response?.data?.message || 'Force charge failed.', 'error');
                                                } finally { setChargeRunning(false); }
                                            }}
                                            style={{
                                                flex: 1, background: '#fff', border: '1px solid #f59e0b', borderRadius: 8,
                                                padding: '8px 10px', fontSize: 12, fontWeight: 600, cursor: chargeRunning ? 'wait' : 'pointer', color: '#b45309',
                                            }}
                                        >
                                            Force re-charge
                                        </button>
                                    </div>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, margin: '4px 0 8px' }}>
                                        Charge history
                                    </div>
                                    {chargeHistory.length === 0 ? (
                                        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>No charges yet for this firm.</div>
                                    ) : (
                                        <div style={{ marginBottom: 16, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                                            {chargeHistory.slice(0, 8).map((c) => {
                                                const pro = Number(c.proRatedAmount || 0);
                                                return (
                                                <div key={c.id} style={{
                                                    display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center',
                                                    padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontSize: 12,
                                                }}>
                                                    <span style={{ color: '#475569' }}>
                                                        {new Date(c.periodStart).toLocaleDateString()}
                                                    </span>
                                                    <span style={{ color: pro > 0 ? '#0f766e' : '#94a3b8', fontSize: 11 }}
                                                        title="Company mid-month discount">
                                                        {pro > 0 ? `−$${pro.toFixed(2)}` : ''}
                                                    </span>
                                                    <span style={{ fontWeight: 700, color: '#1B75F0' }}>
                                                        ${Number(c.totalAmount).toFixed(2)}
                                                    </span>
                                                    <span style={{
                                                        fontWeight: 700,
                                                        color: c.status === 'paid' ? '#16a34a' : c.status === 'failed' ? '#dc2626' : '#92400e',
                                                    }}>
                                                        {c.status}
                                                    </span>
                                                </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Save footer */}
                        <div style={{ padding: '12px 18px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 8 }}>
                            <button onClick={handleSave} disabled={saving} style={{
                                flex: 1, background: '#1B75F0', color: '#fff', border: 'none',
                                borderRadius: 8, padding: '9px 0', fontSize: 13, fontWeight: 600,
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            }}>
                                {saving ? <><Spinner /> Saving…</> : isNew ? 'Create Firm' : 'Save Changes'}
                            </button>
                            <button onClick={closePanel} style={{
                                padding: '9px 16px', background: '#f1f5f9', border: 'none',
                                borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#475569',
                            }}>
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FirmManagement;
